import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Two-mode auth panel: Email/Password (tabs: Login | Register) ODER Google OAuth.
 * Props:
 *   tenantSlug (string, optional) — sets tenant_slug for customer auto-onboarding
 *   onSuccess (function) — called with user object on successful login/register
 *   primaryGradient (string CSS) — gradient for buttons (default: Tonneva green-teal)
 *   googleRedirectPath (string) — destination after Google OAuth
 *   compactGoogle (bool) — show Google as second option below
 */
export default function AuthPanel({ tenantSlug, onSuccess, primaryGradient = "linear-gradient(135deg, #3FA64E 0%, #2A86A8 100%)", googleRedirectPath = "/me" }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ email: "", password: "", name: "" });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);

  const formatError = (e) => {
    const d = e.response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" · ");
    return d?.msg || e.message;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setBusy(true); setErr(null); setSuccess(null);
    try {
      const path = mode === "register" ? "/auth/register" : "/auth/login";
      const payload = { email: f.email.trim().toLowerCase(), password: f.password };
      if (mode === "register" && f.name) payload.name = f.name;
      if (tenantSlug) payload.tenant_slug = tenantSlug;
      const r = await api.post(path, payload);
      if (r.data?.user) {
        localStorage.setItem("tonneva_token", "__cookie__");
        if (mode === "register") {
          setSuccess("Konto erfolgreich erstellt — Sie werden weitergeleitet …");
        }
        const doRedirect = () => {
          if (onSuccess) {
            onSuccess(r.data.user);
          } else {
            const target = tenantSlug ? `/portal/${tenantSlug}/account` : googleRedirectPath;
            navigate(target);
          }
        };
        if (mode === "register") {
          setTimeout(doRedirect, 700);
        } else {
          doRedirect();
        }
      }
    } catch (e) {
      setErr(formatError(e));
    } finally { setBusy(false); }
  };

  const goGoogle = () => {
    sessionStorage.setItem("tonneva_post_login", googleRedirectPath);
    if (tenantSlug) sessionStorage.setItem("tonneva_tenant_slug", tenantSlug);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const forgot = async () => {
    if (!f.email) { setErr("Bitte E-Mail eingeben"); return; }
    try { await api.post("/auth/password-reset-request", { email: f.email }); alert("Wenn ein Konto existiert, haben wir eine E-Mail mit einem Reset-Link gesendet."); } catch (e) { setErr(formatError(e)); }
  };

  return (
    <div>
      {/* Mode tabs */}
      <div className="flex p-1 rounded-full bg-zinc-100 mb-5">
        <button onClick={() => setMode("login")} className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest rounded-full transition ${mode === "login" ? "bg-white shadow text-zinc-900" : "text-zinc-500"}`} data-testid="auth-tab-login">Anmelden</button>
        <button onClick={() => setMode("register")} className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest rounded-full transition ${mode === "register" ? "bg-white shadow text-zinc-900" : "text-zinc-500"}`} data-testid="auth-tab-register">Registrieren</button>
      </div>

      <form onSubmit={submit} className="space-y-3">
        {mode === "register" && (
          <div><Label className="text-xs">Name</Label><Input className="h-11 rounded-md" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="auth-name" /></div>
        )}
        <div><Label className="text-xs">E-Mail</Label><Input type="email" className="h-11 rounded-md" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required data-testid="auth-email" /></div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-xs">Passwort</Label>
            {mode === "login" && <button type="button" onClick={forgot} className="text-xs text-zinc-500 hover:text-zinc-900 underline underline-offset-2" data-testid="auth-forgot">Vergessen?</button>}
          </div>
          <Input type="password" className="h-11 rounded-md" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required minLength={mode === "register" ? 8 : undefined} data-testid="auth-password" />
          {mode === "register" && <p className="text-xs text-zinc-400 mt-1">mind. 8 Zeichen</p>}
        </div>
        {err && <div className="text-sm text-red-600 bg-red-50 rounded-md p-2.5" data-testid="auth-error">{err}</div>}
        {success && <div className="text-sm text-emerald-700 bg-emerald-50 rounded-md p-2.5" data-testid="auth-success">{success}</div>}
        <Button type="submit" disabled={busy} className="w-full text-white rounded-full h-11" style={{ background: primaryGradient }} data-testid="auth-submit">
          {busy ? "…" : (mode === "register" ? "Konto erstellen" : "Anmelden")}
        </Button>
      </form>

      <div className="flex items-center gap-3 my-5 text-xs text-zinc-400">
        <div className="flex-1 h-px bg-zinc-200" /><span>oder</span><div className="flex-1 h-px bg-zinc-200" />
      </div>
      <Button onClick={goGoogle} variant="outline" className="w-full rounded-full h-11 border-2" data-testid="auth-google">
        <svg className="w-4 h-4 mr-3" viewBox="0 0 24 24"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/><path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/></svg>
        Mit Google fortfahren
      </Button>
    </div>
  );
}
